export class UnauthorizedError extends Error {
  constructor() { super('401 Unauthorized') }
}

/** Carries the HTTP status so callers can distinguish 401 (not signed in)
 *  from other errors, and the backend's error message for the callback view. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Who the session resolves to (email/sub). Apex is single-operator, so this is
 *  only ever used to flip RequireAuth from anon → authed. */
export interface Identity {
  email: string
  sub: string
}

/** Where the gate lives (relayed from the backend's GATE_PUBLIC_ORIGIN), and —
 *  when code-handoff is on — Apex's registered client id. client_id present
 *  means RequireAuth does the code-handoff redirect; absent means the legacy
 *  plain-`next` shared-cookie bounce. */
export interface GateStatus {
  enabled: boolean
  gate_origin: string | null
  client_id?: string | null
}

export interface BriefSection {
  repo: string
  branch: string
  current_focus: string
  active_task: string
  recent_commits: string[]
}

export interface Brief {
  generated_at: string
  repo_count: number
  repos: BriefSection[]
  top_memories: string[]
  note: string
}

export interface Memory {
  id: string
  content: string
  type: string
  created_at: string
}

export class DaemonAPI {
  constructor(private baseUrl: string) {}

  private async get<T>(path: string): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, { credentials: 'include' })
    if (res.status === 401) throw new UnauthorizedError()
    if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`)
    return res.json()
  }

  getBrief(): Promise<Brief> {
    return this.get('/brief')
  }

  getMemories(limit = 10): Promise<{ memories: Memory[] }> {
    return this.get(`/memory?limit=${limit}`)
  }

  async chatStream(message: string, sessionId = 'apex'): Promise<ReadableStream<Uint8Array>> {
    const res = await fetch(`${this.baseUrl}/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, session_id: sessionId }),
    })
    if (res.status === 401) throw new UnauthorizedError()
    if (!res.ok) throw new Error(`Chat failed: ${res.status}`)
    if (!res.body) throw new Error('No response body')
    return res.body
  }

  async health(): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/health`, {
        credentials: 'include',
        signal: AbortSignal.timeout(3000),
      })
      return res.ok
    } catch {
      return false
    }
  }

  // ── Auth (one-login / code-handoff) ─────────────────────────────────────────
  //
  // There is no login UI here: the gate is the ONE door. The SPA only probes who
  // the session resolves to (/auth/me), learns where the gate lives
  // (/auth/gate/status), redeems the gate's one-time code (/auth/gate/exchange),
  // and ends the local session (/auth/logout). All carry the session cookie.

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      const message = err?.detail ?? err?.error?.message ?? `${method} ${path} failed: ${res.status}`
      throw new ApiError(message, res.status)
    }
    return res.json()
  }

  /** Who the current session resolves to (401 → not signed in). */
  me(): Promise<Identity> {
    return this.req('GET', '/auth/me')
  }

  /** Where is the gate? client_id, when present, means code-handoff is on. */
  gateStatus(): Promise<GateStatus> {
    return this.req('GET', '/auth/gate/status')
  }

  /** Redeem the gate's one-time code (server-side, via the Daemon). The Daemon
   *  sets its own ``apex_session`` cookie on this response — the SPA never sees
   *  a token — and returns who the session resolves to. */
  gateExchange(code: string, redirectUri: string): Promise<Identity> {
    return this.req('POST', '/auth/gate/exchange', { code, redirect_uri: redirectUri })
  }

  /** End the local session — clears both the Daemon's own cookie and the shared
   *  one. The suite-wide sign-out is still the gate's /logout. */
  logout(): Promise<{ ok: boolean }> {
    return this.req('POST', '/auth/logout', {})
  }
}
